import { useRef, useState } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useStores } from '../../../stores/useStores';
import { Loader } from '../../Loader/Loader';

import { lobbyBannerHasAnimation } from './bannerAnimation';
import { CasinoHeader } from './CasinoHeader';
import styles from './GenericHeader.module.scss';
import RiveHeader from './rive/RiveHeader';
import TocLobbyHeader from './rive/TocLobbyHeader';
import { shownTocRiveUrlAfter, tocLobbyLayers } from './rive/tocLobbyLayers';
import { useTocLobbyBanner } from './rive/useTocLobbyBanner';
import { mainBannerAppearanceKey, useMainBannerImpression } from './useMainBannerImpression';

interface GenericHeaderProps {
  bannerClassName?: string;
  fullHeightAnimation?: boolean;
}

export const GenericHeader = observer((props: GenericHeaderProps) => {
  const { bannerClassName, fullHeightAnimation } = props;
  const { uiStore, coinTypeStore } = useStores();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const tocLobbyBanner = useTocLobbyBanner(isMobile);
  const [shownTocRiveUrl, setShownTocRiveUrl] = useState<string | null>(null);
  // One stable handler per file: a new one each render would re-run the banner's on-screen effect.
  const onShownByUrl = useRef(new Map<string, (reported: string | null) => void>());
  const onTocShown = (reporter: string) => {
    let onShown = onShownByUrl.current.get(reporter);
    if (!onShown) {
      onShown = (reported: string | null) =>
        setShownTocRiveUrl((shown) => shownTocRiveUrlAfter({ shown, reported, reporter }));
      onShownByUrl.current.set(reporter, onShown);
    }
    return onShown;
  };

  const { lobby_cc, lobby_sc, lobby_animation_only } = uiStore.bannerByPage;
  const bgImage = coinTypeStore.coinType === 'GC' ? lobby_cc : lobby_sc;
  // The admin's lobby banner, plain or Rive; not the loader, the ToC banner over it, or the CasinoHeader fallback.
  const mainBanner = !uiStore.bannersLoading && !tocLobbyBanner.riveUrl && lobby_cc && lobby_sc ? bgImage : null;
  const mainBannerRef = useMainBannerImpression(
    mainBanner ? mainBannerAppearanceKey(mainBanner, lobby_animation_only) : null,
  );

  if (uiStore.bannersLoading) {
    return (
      <div className={classNames(styles.loaderWrapper, bannerClassName)}>
        <Loader />
      </div>
    );
  }

  if (tocLobbyBanner.riveUrl) {
    // Keyed by file, so the file on screen keeps its loaded instance while a new one loads over it.
    return (
      <div className={styles.tocLobbyStack}>
        {tocLobbyLayers(tocLobbyBanner.riveUrl, shownTocRiveUrl).map((riveUrl, index) => (
          <TocLobbyHeader
            key={riveUrl}
            bannerClassName={classNames(bannerClassName, index > 0 && styles.tocLobbyOverlay)}
            riveUrl={riveUrl}
            values={tocLobbyBanner.values}
            onShown={onTocShown(riveUrl)}
          />
        ))}
      </div>
    );
  }

  // `!bgImage` never holds once both pages exist (it is one of them); it only narrows bgImage's type.
  if (!lobby_cc || !lobby_sc || !bgImage) {
    return <CasinoHeader />;
  }

  const hasAnimation = lobbyBannerHasAnimation(bgImage, lobby_animation_only);

  return (
    <>
      {uiStore.bannerAnimationFlag && hasAnimation ? (
        <RiveHeader
          animationOnly={lobby_animation_only}
          bannerCC={lobby_cc}
          bannerClassName={bannerClassName}
          bannerRef={mainBannerRef}
          bannerSC={lobby_sc}
          currentCoinType={coinTypeStore.coinType}
          fullHeightAnimation={fullHeightAnimation}
        />
      ) : (
        <picture>
          {bgImage.mobile && <source media="(max-width: 768px)" srcSet={bgImage.mobile} />}
          <img
            ref={mainBannerRef}
            alt="CrownCoinsCasino header"
            className={classNames(styles.banner, bannerClassName)}
            fetchpriority="high"
            src={bgImage.desktop}
          />
        </picture>
      )}
    </>
  );
});
